/**
 * CameraClip: scrubbable HTML5 video element synced to the RSSI waveform cursor.
 *
 * When the user scrubs the video timeline, onTimeUpdate fires with the current
 * playback position in seconds. EventPage passes this to RssiWaveform as the
 * timeCursor prop, keeping the waveform cursor in sync with the video position.
 *
 * Phase 3 assumption: camera_clip_url is either a presigned Spaces URL or a
 * public-read Spaces URL. If the video fails to load (expired presigned URL),
 * an error placeholder is shown.
 *
 * Clip shows with controls visible and autoPlay disabled so the user must
 * explicitly start playback (accessibility + unexpected autoplay prevention).
 */
import { useState } from 'react';
import * as styles from './CameraClip.css';

/** Props for CameraClip. */
interface Props {
  /** Presigned or public-read URL to the MP4/WebM camera clip. */
  url: string;
  /** Poster image URL (thumbnail); shown before playback starts. */
  thumbnailUrl: string | null;
  /** Callback fired on every timeupdate event with currentTime in seconds. */
  onTimeUpdate: (currentTime: number) => void;
}

/**
 * Scrubbable camera clip player.
 *
 * Fires onTimeUpdate on every HTMLVideoElement timeupdate event so the
 * RssiWaveform cursor stays synchronized.
 *
 * @param props - url, thumbnailUrl, onTimeUpdate
 */
export function CameraClip({ url, thumbnailUrl, onTimeUpdate }: Props) {
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <div className={styles.errorMessage} data-testid="camera-clip-error">
        Clip no longer available. Ask your administrator to re-upload.
      </div>
    );
  }

  return (
    <div className={styles.videoContainer} data-testid="camera-clip">
      <video
        className={styles.video}
        src={url}
        poster={thumbnailUrl ?? undefined}
        controls
        muted
        autoPlay={false}
        preload="metadata"
        onTimeUpdate={(e) => {
          onTimeUpdate((e.target as HTMLVideoElement).currentTime);
        }}
        onError={() => setLoadError(true)}
        data-testid="camera-video"
      >
        Your browser does not support HTML5 video.
      </video>
    </div>
  );
}
